import { useEffect, useState } from 'react';

import { decryptFileHeader, decryptText, UnsupportedVersionError, type FileHeader } from '@/crypto';
import type { Message } from '@/api/types';

/** What a message holds once its ciphertext has been opened on this device. */
export type MessageContent = { kind: 'pending' } | { kind: 'text'; text: string } | { kind: 'file'; header: FileHeader } | { kind: 'error'; reason: 'key' | 'version' };

/**
 * Opens a message: the text, or the header of a file. The ciphertext is bound to the bridge, the message id and the
 * side that wrote it, so a message the server moved, replayed or relabelled opens as `error`.
 */
export function useMessageContent(message: Message, bridgeKey: CryptoKey, bridgeId: string): MessageContent {
  const [content, setContent] = useState<MessageContent>({ kind: 'pending' });

  // leaving the page that shows the bridge drops what was decrypted from its state
  useEffect(
    () => () => {
      setContent({ kind: 'pending' });
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const binding = { bridgeId, messageId: message.id, from: message.from };
    const run = async (): Promise<void> => {
      try {
        if (message.kind === 'text') {
          const text = await decryptText(bridgeKey, message.body, binding);
          if (!cancelled) setContent({ kind: 'text', text });
        } else {
          const header = await decryptFileHeader(bridgeKey, message.header, binding);
          if (!cancelled) setContent({ kind: 'file', header });
        }
      } catch (cause) {
        if (!cancelled) setContent({ kind: 'error', reason: cause instanceof UnsupportedVersionError ? 'version' : 'key' });
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [message, bridgeKey, bridgeId]);

  return content;
}
